import { useRef } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Eye, Download, Upload, RefreshCw } from "lucide-react";

// Slot config per module — which documents live on each record type.
export const DOC_SLOTS = {
  leases: [{ slot: "lease", label: "Lease" }],
  insurance: [
    { slot: "policy", label: "Policy" },
    { slot: "invoice", label: "Invoice" },
    { slot: "receipt", label: "Receipt" },
  ],
  taxes: [
    { slot: "tax_bill", label: "Tax Bill" },
    { slot: "receipt", label: "Receipt" },
  ],
};

async function fetchDoc(id) {
  const { data } = await api.get(`/documents/${id}`);
  return data;
}

// Mirror of the backend allowlist — only these MIME types may ever be rendered.
const ALLOWED_MIME = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

// Convert a validated base64 data URL into a safe Blob object URL.
// Returns null if the payload is missing or its MIME type is not allowed.
function dataUrlToSafeBlobUrl(dataUrl) {
  const m = /^data:([\w.+-]+\/[\w.+-]+);base64,(.*)$/s.exec(dataUrl || "");
  if (!m) return null;
  const mime = m[1].toLowerCase();
  if (!ALLOWED_MIME.has(mime)) return null;
  try {
    const bytes = atob(m[2]);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return URL.createObjectURL(new Blob([arr], { type: mime }));
  } catch {
    return null;
  }
}

export async function viewDoc(id) {
  try {
    const doc = await fetchDoc(id);
    if (!doc.data) { toast.info("This is a demo record — no file attached yet."); return; }
    const url = dataUrlToSafeBlobUrl(doc.data);
    if (!url) { toast.error("This file type cannot be previewed safely."); return; }
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error("Could not open document");
  }
}

export async function downloadDoc(id) {
  try {
    const doc = await fetchDoc(id);
    if (!doc.data) { toast.info("This is a demo record — no file attached yet."); return; }
    const url = dataUrlToSafeBlobUrl(doc.data);
    if (!url) { toast.error("This file type is not allowed."); return; }
    const a = document.createElement("a");
    a.href = url; a.download = doc.name || "document"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error("Could not download document");
  }
}

// Compact View/Download pair for a known doc id (used in alerts & dashboard).
export function DocLinks({ docId, label }) {
  if (!docId) return null;
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => viewDoc(docId)} data-testid={`doclink-view-${docId}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
        <Eye className="w-3.5 h-3.5" /> View {label}
      </button>
      <button onClick={() => downloadDoc(docId)} data-testid={`doclink-download-${docId}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
        <Download className="w-3.5 h-3.5" /> Download
      </button>
    </div>
  );
}

// Full per-record document cell (used in Lease / Insurance / Tax tables).
export default function DocActions({ moduleKey, record, canWrite, onChange }) {
  const slots = DOC_SLOTS[moduleKey] || [];
  const inputs = useRef({});

  const upload = (slot, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max file size 5MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/attachments", {
          parent_collection: moduleKey, parent_id: record.id, slot, name: file.name,
          file_type: file.name.split(".").pop().toLowerCase(),
          size_kb: Math.round(file.size / 1024), data: reader.result,
        });
        toast.success("Document uploaded");
        onChange && onChange();
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {slots.map(({ slot, label }) => {
        const docId = record[`${slot}_doc_id`];
        const key = `${record.id}-${slot}`;
        return (
          <div key={key} className="flex items-center gap-2 flex-wrap">
            <input
              ref={(el) => (inputs.current[slot] = el)} type="file" className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={(e) => { upload(slot, e.target.files[0]); e.target.value = ""; }}
              data-testid={`fileinput-${moduleKey}-${slot}-${record.id}`}
            />
            {docId ? (
              <>
                <button onClick={() => viewDoc(docId)} data-testid={`view-${moduleKey}-${slot}-${record.id}`} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                  <Eye className="w-3.5 h-3.5" /> {label}
                </button>
                <button onClick={() => downloadDoc(docId)} data-testid={`download-${moduleKey}-${slot}-${record.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
                  <Download className="w-3.5 h-3.5" />
                </button>
                {canWrite && (
                  <button onClick={() => inputs.current[slot]?.click()} title={`Replace ${label}`} data-testid={`replace-${moduleKey}-${slot}-${record.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                )}
              </>
            ) : canWrite ? (
              <button onClick={() => inputs.current[slot]?.click()} data-testid={`upload-${moduleKey}-${slot}-${record.id}`} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                <Upload className="w-3.5 h-3.5" /> Upload {label}
              </button>
            ) : (
              <span className="text-xs text-muted-foreground">No {label}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
